"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export interface Position {
  x: number;
  y: number;
}

interface DragStart {
  x: number;
  y: number;
  posX: number;
  posY: number;
}

/** Position origin determines how delta is applied */
export type PositionOrigin = "top-left" | "bottom-right";

/** Stored mouse handlers for cleanup on unmount */
interface MouseHandlers {
  move: (e: MouseEvent) => void;
  up: () => void;
}

interface UseDraggableOptions {
  /** Ref to the draggable DOM element. Touch listeners are attached natively with { passive: false } to allow preventDefault. */
  elementRef: React.RefObject<HTMLElement | null>;
  /** Whether dragging is enabled */
  enabled: boolean;
  /** Current position */
  position: Position;
  /** Callback when position changes during drag */
  onPositionChange: (position: Position) => void;
  /** Callback when drag ends with final position */
  onDragEnd?: (position: Position) => void;
  /** Function to clamp position within bounds */
  clampPosition?: (position: Position) => Position;
  /** Minimum movement in pixels before drag starts (prevents accidental drags on taps) */
  dragThreshold?: number;
  /** Position origin - 'top-left' adds delta, 'bottom-right' subtracts delta (default: 'bottom-right') */
  positionOrigin?: PositionOrigin;
  /** Enable mouse drag support for desktop (default: false) */
  enableMouse?: boolean;
}

interface UseDraggableReturn {
  /** Whether currently dragging */
  isDragging: boolean;
  /** Mouse down handler (only useful if enableMouse is true) */
  handleMouseDown: (e: React.MouseEvent) => void;
}

/**
 * Hook for making an element draggable via touch and optionally mouse.
 * Touch listeners are attached natively with `{ passive: false }` so that
 * `preventDefault()` can stop page scrolling during drag on mobile.
 *
 * @example
 * ```tsx
 * const ref = useRef<HTMLDivElement>(null);
 * const { isDragging, handleMouseDown } = useDraggable({
 *   elementRef: ref,
 *   enabled: true,
 *   position,
 *   onPositionChange: setPosition,
 *   clampPosition,
 *   positionOrigin: "top-left",
 *   enableMouse: true,
 * });
 * return <div ref={ref} onMouseDown={handleMouseDown}>...</div>;
 * ```
 */
export function useDraggable({
  elementRef,
  enabled,
  position,
  onPositionChange,
  onDragEnd,
  clampPosition = (p) => p,
  dragThreshold = 5,
  positionOrigin = "bottom-right",
  enableMouse = false,
}: UseDraggableOptions): UseDraggableReturn {
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<DragStart | null>(null);
  const isDraggingRef = useRef(false);
  const mouseHandlersRef = useRef<MouseHandlers | null>(null);

  // Bridge elementRef.current -> state so the touch-listener effect below
  // re-runs once the element mounts. A ref's `.current` changing does not
  // retrigger an effect, so when the draggable mounts late (e.g. rendered
  // only after a localStorage/async gate flips), the touch listeners would
  // otherwise stay unbound until some unrelated re-render. This syncs with the
  // DOM node, an external system, so the effect fires exactly when it appears.
  const [element, setElement] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (element !== elementRef.current) setElement(elementRef.current);
  });

  // Keep refs in sync for use in document event listeners
  const positionRef = useRef(position);
  const clampPositionRef = useRef(clampPosition);
  const onPositionChangeRef = useRef(onPositionChange);
  const onDragEndRef = useRef(onDragEnd);

  useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging]);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    clampPositionRef.current = clampPosition;
  }, [clampPosition]);

  useEffect(() => {
    onPositionChangeRef.current = onPositionChange;
  }, [onPositionChange]);

  useEffect(() => {
    onDragEndRef.current = onDragEnd;
  }, [onDragEnd]);

  const calculateNewPosition = useCallback(
    (deltaX: number, deltaY: number, startPos: DragStart): Position => {
      const clamp = clampPositionRef.current;
      if (positionOrigin === "top-left") {
        return clamp({
          x: startPos.posX + deltaX,
          y: startPos.posY + deltaY,
        });
      }
      // bottom-right: invert the delta
      return clamp({
        x: startPos.posX - deltaX,
        y: startPos.posY - deltaY,
      });
    },
    [positionOrigin],
  );

  // Native touch listeners attached with { passive: false } so preventDefault() works.
  // React's synthetic touch events are passive by default (React 17+), which makes
  // preventDefault() a no-op and allows the page to scroll during drag.
  useEffect(() => {
    if (!element || !enabled) return;

    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;

      const pos = positionRef.current;
      dragStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        posX: pos.x,
        posY: pos.y,
      };
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!dragStartRef.current) return;
      const touch = e.touches[0];
      if (!touch) return;

      const deltaX = touch.clientX - dragStartRef.current.x;
      const deltaY = touch.clientY - dragStartRef.current.y;

      // Only start dragging if moved more than threshold (to allow normal taps)
      if (!isDraggingRef.current && Math.abs(deltaX) < dragThreshold && Math.abs(deltaY) < dragThreshold) {
        return;
      }

      // This works because the listener is non-passive
      e.preventDefault();
      setIsDragging(true);

      const newPos = calculateNewPosition(deltaX, deltaY, dragStartRef.current);
      onPositionChangeRef.current(newPos);
    };

    const onTouchEnd = () => {
      if (dragStartRef.current && isDraggingRef.current) {
        onDragEndRef.current?.(positionRef.current);
      }
      dragStartRef.current = null;

      // Delay resetting isDragging to prevent click from firing
      setTimeout(() => setIsDragging(false), 100);
    };

    element.addEventListener("touchstart", onTouchStart, { passive: true });
    element.addEventListener("touchmove", onTouchMove, { passive: false });
    element.addEventListener("touchend", onTouchEnd);

    return () => {
      element.removeEventListener("touchstart", onTouchStart);
      element.removeEventListener("touchmove", onTouchMove);
      element.removeEventListener("touchend", onTouchEnd);
    };
  }, [element, enabled, dragThreshold, calculateNewPosition]);

  // Cleanup function for mouse handlers (used by handleMouseUp and unmount effect)
  const cleanupMouseHandlers = useCallback(() => {
    if (mouseHandlersRef.current) {
      document.removeEventListener("mousemove", mouseHandlersRef.current.move);
      document.removeEventListener("mouseup", mouseHandlersRef.current.up);
      mouseHandlersRef.current = null;
    }
  }, []);

  // Mouse handlers (for desktop) - use positionRef to avoid recreating on every position change
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!enabled || !enableMouse) return;
      e.preventDefault();

      const pos = positionRef.current;
      const startPos = {
        x: e.clientX,
        y: e.clientY,
        posX: pos.x,
        posY: pos.y,
      };
      dragStartRef.current = startPos;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!dragStartRef.current) return;

        const deltaX = moveEvent.clientX - dragStartRef.current.x;
        const deltaY = moveEvent.clientY - dragStartRef.current.y;

        if (!isDraggingRef.current && Math.abs(deltaX) < dragThreshold && Math.abs(deltaY) < dragThreshold) {
          return;
        }

        setIsDragging(true);

        const newPos = calculateNewPosition(deltaX, deltaY, dragStartRef.current);
        onPositionChangeRef.current(newPos);
      };

      const handleMouseUp = () => {
        if (dragStartRef.current && isDraggingRef.current) {
          onDragEndRef.current?.(positionRef.current);
        }
        dragStartRef.current = null;
        setTimeout(() => setIsDragging(false), 100);
        cleanupMouseHandlers();
      };

      // Store handlers for cleanup on unmount
      mouseHandlersRef.current = { move: handleMouseMove, up: handleMouseUp };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [enabled, enableMouse, dragThreshold, calculateNewPosition, cleanupMouseHandlers],
  );

  // Cleanup mouse handlers on unmount to prevent memory leaks
  useEffect(() => {
    return cleanupMouseHandlers;
  }, [cleanupMouseHandlers]);

  return {
    isDragging,
    handleMouseDown,
  };
}
